/**
 * ForwardModal - Encaminhar mensagens estilo WhatsApp.
 *
 * Recebe as mensagens já selecionadas (apenas a contagem, para o cabeçalho),
 * a lista de destinos normalizada (`[{ id, name, phone, avatarUrl }]`) e um
 * callback assíncrono `onForward(destinosSelecionados)` que faz o reenvio de
 * fato no canal correspondente (wa_personal / wa_business). O modal é
 * agnóstico ao canal — toda a diferença de serviço/shape mora no ChatWindow.
 *
 * UI: busca por nome/número + lista com multi-seleção (checkbox) + botão
 * "Encaminhar". Mostra progresso e resultado por destino. Espelha a linguagem
 * visual do TransferContactModal (overlay, container, header, footer).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Send, Check, Loader2, Search, User, AlertCircle } from 'lucide-react';

const ForwardModal = ({
  isOpen,
  onClose,
  messageCount = 0,
  destinations = [],
  onForward,            // async (selectedDestinations) => [{ id, ok, error? }]
  colors,
  displayMode = 'modal',
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  // results: { done, total, failures: [{ name }] } | null
  const [results, setResults] = useState(null);

  // Reset completo ao (re)abrir — o modal fica montado entre usos.
  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setSelectedIds(new Set());
    setSending(false);
    setResults(null);
  }, [isOpen]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return destinations;
    const digits = term.replace(/\D/g, '');
    return destinations.filter((d) => {
      const name = (d.name || '').toLowerCase();
      const phone = (d.phone || '').replace(/\D/g, '');
      return name.includes(term) || (digits && phone.includes(digits));
    });
  }, [destinations, search]);

  const toggle = (id) => {
    if (sending) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleForward = async () => {
    if (sending || selectedIds.size === 0) return;
    const selected = destinations.filter((d) => selectedIds.has(d.id));
    if (selected.length === 0) return;

    setSending(true);
    setResults({ done: 0, total: selected.length, failures: [] });

    let done = 0;
    const failures = [];
    try {
      const outcomes = await onForward(selected, (completed) => {
        // Progresso incremental opcional vindo do handler.
        setResults((prev) => (prev ? { ...prev, done: completed } : prev));
      });

      // Se o handler retornou um array de resultados por destino, usamos ele
      // para contabilizar falhas; senão consideramos tudo sucesso.
      if (Array.isArray(outcomes)) {
        outcomes.forEach((r) => {
          done += 1;
          if (r && r.ok === false) {
            failures.push({ name: r.name || '' });
          }
        });
      } else {
        done = selected.length;
      }
    } catch (err) {
      console.error('[ForwardModal] forward failed:', err);
      // Falha geral inesperada — marca todos os selecionados como falha.
      selected.forEach((d) => failures.push({ name: d.name || d.phone || '' }));
      done = selected.length;
    } finally {
      setResults({ done, total: selected.length, failures });
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const isDrawer = displayMode === 'drawer';
  const allDone = results && !sending;
  const successCount = results ? results.total - results.failures.length : 0;

  const initials = (name, phone) => {
    const base = (name || phone || '?').trim();
    return base ? base[0].toUpperCase() : '?';
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex',
        alignItems: isDrawer ? 'stretch' : 'center',
        justifyContent: isDrawer ? 'flex-end' : 'center',
        padding: isDrawer ? 0 : '20px',
      }}
      onClick={sending ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: isDrawer ? 0 : '16px',
          width: isDrawer ? 420 : '100%',
          maxWidth: isDrawer ? '100%' : '440px',
          overflow: 'hidden',
          boxShadow: isDrawer
            ? '-8px 0 32px rgba(0,0,0,0.25)'
            : '0 20px 60px rgba(0,0,0,0.3)',
          height: isDrawer ? '100%' : 'auto',
          maxHeight: isDrawer ? '100%' : '85vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <Send size={18} color={colors.accentPrimary} /> Encaminhar
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textSecondary }}>
              {messageCount === 1
                ? '1 mensagem selecionada'
                : `${messageCount} mensagens selecionadas`}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              background: 'none', border: 'none',
              cursor: sending ? 'not-allowed' : 'pointer',
              color: colors.textSecondary, padding: '8px', borderRadius: '8px',
              opacity: sending ? 0.5 : 1,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '16px 20px', display: 'flex', flexDirection: 'column',
          gap: '12px', overflowY: 'auto', flex: 1,
        }}>
          {/* Resultado final */}
          {allDone && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 6,
              padding: '10px 12px', borderRadius: 8,
              backgroundColor: results.failures.length === 0
                ? 'rgba(16,185,129,0.12)'
                : 'rgba(245,158,11,0.12)',
              border: `1px solid ${results.failures.length === 0
                ? 'rgba(16,185,129,0.4)'
                : 'rgba(245,158,11,0.4)'}`,
            }}>
              <span style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 600,
                color: results.failures.length === 0 ? '#059669' : '#b45309',
              }}>
                {results.failures.length === 0
                  ? <Check size={15} />
                  : <AlertCircle size={15} />}
                {successCount > 0
                  ? `Encaminhado para ${successCount} ${successCount === 1 ? 'destino' : 'destinos'}.`
                  : 'Não foi possível encaminhar.'}
              </span>
              {results.failures.length > 0 && (
                <span style={{ fontSize: 12, color: colors.textSecondary }}>
                  Falha em {results.failures.length}{' '}
                  {results.failures.length === 1 ? 'destino' : 'destinos'}.
                </span>
              )}
            </div>
          )}

          {/* Busca */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            backgroundColor: colors.bgSecondary, borderRadius: '8px',
            padding: '0 12px', border: `1px solid ${colors.borderColor}`,
          }}>
            <Search size={16} color={colors.textTertiary} style={{ flexShrink: 0 }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou número"
              disabled={sending}
              style={{
                flex: 1, padding: '10px 0', border: 'none', background: 'transparent',
                fontSize: '13px', color: colors.textPrimary, outline: 'none',
              }}
            />
          </div>

          {/* Lista de destinos */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.length === 0 ? (
              <span style={{ fontSize: '13px', color: colors.textTertiary, padding: '12px 4px' }}>
                Nenhum contato encontrado.
              </span>
            ) : (
              filtered.map((d) => {
                const isChecked = selectedIds.has(d.id);
                return (
                  <button
                    key={d.id}
                    onClick={() => toggle(d.id)}
                    disabled={sending}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 10px',
                      background: isChecked ? `${colors.accentPrimary}12` : 'transparent',
                      border: 'none', borderRadius: '8px',
                      cursor: sending ? 'not-allowed' : 'pointer',
                      textAlign: 'left', transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isChecked && !sending) {
                        e.currentTarget.style.backgroundColor = colors.bgSecondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isChecked
                        ? `${colors.accentPrimary}12` : 'transparent';
                    }}
                  >
                    {/* Avatar */}
                    <span style={{
                      width: 36, height: 36, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: colors.bgTertiary, color: colors.textTertiary,
                      flexShrink: 0, overflow: 'hidden', fontSize: 14, fontWeight: 600,
                    }}>
                      {d.avatarUrl
                        ? <img src={d.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (d.name || d.phone ? initials(d.name, d.phone) : <User size={16} />)}
                    </span>

                    {/* Nome + número */}
                    <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: '14px', color: colors.textPrimary,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {d.name || d.phone || 'Desconhecido'}
                      </span>
                      {d.phone && d.name && (
                        <span style={{ fontSize: '11px', color: colors.textTertiary }}>
                          {d.phone}
                        </span>
                      )}
                    </span>

                    {/* Checkbox */}
                    <span style={{
                      width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `2px solid ${isChecked ? colors.accentPrimary : colors.borderColor}`,
                      backgroundColor: isChecked ? colors.accentPrimary : 'transparent',
                      transition: 'all 0.15s',
                    }}>
                      {isChecked && <Check size={13} color="white" />}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 12, padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '12px', color: colors.textSecondary }}>
            {selectedIds.size > 0
              ? `${selectedIds.size} ${selectedIds.size === 1 ? 'destino' : 'destinos'}`
              : 'Selecione os destinos'}
          </span>
          {allDone ? (
            <button
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 18px', backgroundColor: colors.accentPrimary, color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Concluído
            </button>
          ) : (
            <button
              onClick={handleForward}
              disabled={sending || selectedIds.size === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 18px',
                backgroundColor: colors.accentPrimary, color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                cursor: (sending || selectedIds.size === 0) ? 'not-allowed' : 'pointer',
                opacity: (sending || selectedIds.size === 0) ? 0.6 : 1,
              }}
            >
              {sending ? (
                <>
                  <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
                  {results ? `Enviando ${results.done}/${results.total}…` : 'Enviando…'}
                </>
              ) : (
                <>
                  <Send size={15} />
                  Encaminhar
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForwardModal;
